import classNames from 'classnames';
import { Tooltip, message, Card } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CloseCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { UserHttp } from '@/services';
import { useCurrentReservesContext, useUserContext } from '@/context';
import { useDevice, useModal, useRefresh } from '@/hooks';
import { Flex, InputPlus, LoadingSpin, PageContainer, Permission } from '@/components';
import LogTable, { LogTableColumn } from '@/components/common/LogTable';
import appConfirm from '@/utils/appConfirm';
import { useTranslation } from 'react-i18next';
import { ColumnBuilder } from '@/utils';
import { useTodayReserve } from '@/hooks';
import { TfiViewListAlt } from "react-icons/tfi";
import { ReserveInfoModal } from '@/components/modals';

export function NurtureTeammate() {

  const [t] = useTranslation(['common']);
  const { profile } = useUserContext();
  const [refreshCounter, refresh] = useRefresh();
  const [loading, setLoading] = useState(true);
  const [friends, setFriends] = useState<TeammateEntity[]>([]);
  const { items, loading: reservesLoading } = useCurrentReservesContext();
  const { currentUserMealTypes, teammateMealTypes, teammatesReserve, teammatesReservesCount, currentUserReservesCount } = useTodayReserve({ items });

  const modal = useModal();
  const { isDesktop } = useDevice();

  const getStatus = useCallback((value: string) => {
    if (value === 'FINAL') return t('common:labels.status-accepted');
    if (value === 'WAITING') return t('common:labels.status-in-progress');
    return undefined;
  }, [t]);

  const getRejectTitle = useCallback((isCurrentUser: boolean, isWaiting: boolean) => {
    if (!isCurrentUser) {
      return t('common:labels.delete');
    }
    return isWaiting ? t('common:labels.reject') : t('common:labels.leave-group');
  }, [t]);

  const columns = useMemo(() => ColumnBuilder.create([
    {
      title: t('common:labels.username'),
      dataIndex: 'username',
      key: 'username',
      // fixed: 'right'
    },
    {
      title: t('common:labels.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('common:labels.status'),
      dataIndex: 'status',
      key: 'status',
      align: 'center'
    },
    {
      title: t('common:labels.reserve-count'),
      dataIndex: 'reserveCount',
      key: 'reserveCount',
      align: 'center'
    },
    {
      title: t('common:labels.action'),
      dataIndex: 'op',
      key: 'op',
      align: 'center'
    },
  ]).build() as LogTableColumn[], [t]);


  const handleAdd = useCallback(async (value: string) => {
    if (value.trim() === '') return;
    await UserHttp.addTeammate(value);
    message.success(t('reserve:messages.success-add-teammate'));
    refresh();
  }, [refresh, t]);

  const handleRemove = useCallback((item: TeammateEntity, isCurrentUser: boolean) => {
    const content = isCurrentUser ? t('reserve:messages.confirm-leave-team') : t('reserve:messages.confirm-remove-teammate');
    appConfirm({
      icon: <CloseCircleOutlined style={{ color: 'red' }} />,
      content,
      okType: 'danger',
      onOk: async () => {
        try {
          await UserHttp.removeTeammate(item.id);
          message.success({ content: t('reserve:messages.success-remove-teammate') });
          refresh();
        }
        catch (e) {
        }
      }
    });
  }, [refresh, t]);

  const handleAccept = useCallback((item: TeammateEntity) => {
    appConfirm({
      content: t('reserve:messages.confirm-join-team'),
      icon: <CheckCircleOutlined style={{ color: '#43bf58' }} />,
      onOk: async () => {
        try {
          await UserHttp.joinTeammate(item.id);
          message.success({ content: t('reserve:messages.success-add-teammate') });
          refresh();
        }
        catch (e) {
        }
      }
    });
  }, [refresh, t]);

  const getTeammateReserveInfo = useCallback((isCurrentUser: boolean, item: TeammateEntity) => {
    const reserves = teammatesReserve[item?.user?.username];
    const mealTypes = [];
    if (isCurrentUser) {
      currentUserMealTypes.forEach(mealType => {
        if (mealType?.reserve?.username === item?.user?.username) {
          mealTypes.push(mealType);
        }
      });
    }
    else {
      teammateMealTypes.forEach(mealType => {
        if (mealType?.teammateReserve?.username === item?.user?.username) {
          mealTypes.push(mealType);
        }
      });
    }
    return { mealTypes, teammate: item, report: reserves };
  }, []);

  const handleViewReserve = useCallback(({ mealTypes, teammate, report }) => {
    modal.show({ mealTypes, teammate, report });
  }, [teammatesReserve]);

  useEffect(() => {
    setLoading(true);
    UserHttp.listTeammate()
      .then(({ payload }) => setFriends(payload?.content ?? []))
      .finally(() => setLoading(false));
  }, [refreshCounter]);

  const rejectAction = useMemo(() => ({ isCurrentUser, item }) => (
    <span
      onClick={handleRemove.bind({}, item, isCurrentUser)}
      className="flex-center cursor-pointer text-xl text-red-500 flex items-center">
      <CloseCircleOutlined />
    </span>
  ), [handleRemove]);

  const acceptAction = useMemo(() => ({ item }) => (
    <span
      onClick={handleAccept.bind({}, item)}
      className="flex-center cursor-pointer text-xl text-primary-food">
      <CheckCircleOutlined />
    </span>
  ), [handleAccept]);

  const viewReserveAction = useMemo(() => ({ isCurrentUser, item }) => {
    const info = getTeammateReserveInfo(isCurrentUser, item);
    if (!info.report || info?.report?.length === 0) {
      return <></>;
    }
    return (
      <span
        onClick={() => handleViewReserve(info)}
        className="flex-center cursor-pointer text-xl text-app-blue">
        <TfiViewListAlt />
      </span>
    );
  }, [getTeammateReserveInfo, handleViewReserve]);

  const dataSource = friends?.map((item, i) => {
    const { user, status } = item;
    const isCurrentUser = profile?.username === item?.user?.username;
    const isWaiting = item.status === 'WAITING';

    let count = teammatesReservesCount?.[user.username] ?? 0;
    if (count == 0 && currentUserReservesCount?.[user.username]) {
      count = currentUserReservesCount?.[user.username];
    }

    return {
      key: String(i),
      username: user?.username,
      name: `${user.firstName} ${user.lastName}`,
      status: getStatus(status) ?? status,
      reserveCount: count,
      statusEn: status,
      op: (
        <Flex direction="row" align="center" justify="center">
          <Tooltip title="مشاهده رزروها">
            {viewReserveAction({ isCurrentUser, item })}
          </Tooltip>
          {(isCurrentUser || item.status === 'WAITING' || profile?.canDeleteTeammates) &&
            <Tooltip
              title={getRejectTitle(isCurrentUser, isWaiting)}>
              {rejectAction({ isCurrentUser, item })}
            </Tooltip>
          }
          {isCurrentUser && status !== 'FINAL' && (
            <Tooltip trigger={['hover']} title={t('common:labels.accept')}>
              {acceptAction({ item })}
            </Tooltip>
          )}
        </Flex>
      ),
      opNoTooltip: (
        <Flex direction="row" align="center" justify="center">
          {rejectAction({ isCurrentUser, item })}
          {isCurrentUser && status !== 'FINAL' && (acceptAction({ item }))}
        </Flex>
      )
    };
  });

  return (
    <Permission required="ROLE_RS_USER_TEAMMATES">
      <PageContainer title={t('common:features.teammate')}>
        <ReserveInfoModal variant="nurture" {...modal} />
        <Flex className="app-nurture-teammate text-app-gray-darker h-full" direction="column">
          <div>
            {t('common:messages.enter-teammate-username')}
          </div>
          <InputPlus
            maxWidth={350}
            onSubmit={handleAdd}
            placeholder={t('common:labels.student-number')}
          />
          <Flex className="h-full" direction="column" gap={16}>
            {loading || reservesLoading ?
              <LoadingSpin className="mt-5" /> :
              isDesktop() ?
                <LogTable
                  className="text-xs"
                  pagination={false}
                  scroll={{ x: 400 }}
                  rows={dataSource}
                  columns={columns}
                /> :
                (
                  dataSource.map((item, key) => (
                    <Card
                      className={'relative'}
                      size="small"
                      key={key}
                    >
                      <Flex
                        align="center"
                        gap={0}
                        // style={{ transform: 'translateX(-50%)' }}
                        className={classNames(
                          'p-1 rounded text-xs absolute top-[-8px] left-[8px] h-[16px]',
                          { 'bg-primary-food': item.statusEn === 'FINAL' },
                          { 'bg-accent': item.statusEn !== 'FINAL' },
                        )}>
                        <span className="text-white text-xs">
                          {item.status}
                        </span>
                      </Flex>
                      <Flex align="center">
                        <Flex direction="column">
                          <span>{item.name}</span>
                          <span>{item.username}</span>
                          <span>{t('common:labels.reserve-count')}: {item.reserveCount}</span>
                        </Flex>
                        <span className="ms-auto">{item.opNoTooltip}</span>
                      </Flex>
                    </Card>
                  ))
                )
            }
          </Flex>
        </Flex>
      </PageContainer>
    </Permission>
  );
}

export default NurtureTeammate;
